/**
 * Client approval trail — pure builder shared by the client portal, the staff
 * read-only embed, and the PM "Client view" pop-out. One builder, one component,
 * so the staff preview can never diverge from what the client sees.
 *
 * Model
 *  - An **item** is one client-facing release: a bundle when the release belongs to
 *    one, otherwise the single release. Items carry a phase (Concept / Engineering),
 *    a media (2D / 3D), and a structure *scope* (set of structures; empty = whole
 *    project). Approving an item approves the whole set at once.
 *  - A **station** is a (phase, media) stop on the timeline: Concept 2D → Concept 3D
 *    → Engineering 2D → Engineering 3D. Only stations that exist somewhere on the
 *    project are drawn, so a post-contract-only portal never shows empty concept
 *    stops.
 *  - The **trunk** is the whole-project lane. When structure-scoped items exist the
 *    trunk forks (`forkAt`) into one **lane** per structure set that has a release,
 *    plus one lane for every other proposal structure. Those extra lanes inherit the
 *    trunk, so a sign-off made before the split still shows on each structure.
 *    A lane whose structure set is a strict superset of another's is that lane's
 *    parent (a later split nests the same way).
 *
 * Legacy: portals created before per-release approvals existed are matched through
 * `findClientApprovalForFileReleases` against the `plansApproval` / `modelApproval`
 * / `conceptApproval` slots, and older unapproved items of the same (scope, phase,
 * media) are derived as superseded even when `supersededByReleaseId` is absent.
 * A release asks for a signature only when `approvalRequested` is explicitly true.
 * A missing flag (older rows, or a release saved before the flag was written) is
 * informational.
 */

import type {
  PortalDeliverableApproval,
  PortalDesignPhase,
  PortalFileRelease,
  PortalReleaseBundle,
  ProjectFile,
  ProjectFileStream,
} from '../types';
import {
  findClientApprovalForFileReleases,
  type PortalApprovalTargetGroup,
} from './portalDeliverableApprovalService';
import {
  PORTAL_DESIGN_PHASE_LABELS,
  PORTAL_DESIGN_PHASES,
  portalReleaseDesignPhase,
  releaseMovedToStream,
  resolveBundleStructureScopes,
  resolveReleaseStructureScopes,
  wholeProjectScope,
  type ReleaseStructureScope,
  type ResolveScopeContext,
} from './portalReleasePhase';
import { structureSelectionLabel, structureSetKey, WHOLE_PROJECT_SELECTION_LABEL } from './structureSelectionLabel';
import { effectiveInheritVersionsAfter } from './projectFileStreamVersions';
import type { SharedModelGroup } from './sharedModelApproval';

export type TrailMedia = '2d' | '3d';

export const TRAIL_MEDIA_LABELS: Record<TrailMedia, string> = { '2d': '2D', '3d': '3D' };

export type TrailItemStatus =
  /** Client signed off on this exact item. */
  | 'approved'
  /** Current item, approval requested, not yet signed. */
  | 'awaiting'
  /** Current item released for information only (no approval requested). */
  | 'informational'
  /** Replaced by a newer item before approval. */
  | 'superseded';

export type TrailStationStatus =
  | 'approved'
  | 'awaiting'
  | 'informational'
  /** Covered by an approval on the trunk / a parent lane (drawn once, inherited). */
  | 'shared'
  /** Nothing released for this stop in this lane yet. */
  | 'pending';

export interface TrailStationKey {
  phase: PortalDesignPhase;
  media: TrailMedia;
}

/** Canonical station order. */
export const TRAIL_STATION_ORDER: TrailStationKey[] = [
  { phase: 'concept', media: '2d' },
  { phase: 'concept', media: '3d' },
  { phase: 'engineering', media: '2d' },
  { phase: 'engineering', media: '3d' },
];

export function trailStationId(k: TrailStationKey): string {
  return `${k.phase}:${k.media}`;
}

export function trailStationLabel(k: TrailStationKey): string {
  return `${PORTAL_DESIGN_PHASE_LABELS[k.phase]} ${TRAIL_MEDIA_LABELS[k.media]}`;
}

export interface TrailItem {
  /** Bundle id when bundled, else release id. */
  id: string;
  media: TrailMedia;
  phase: PortalDesignPhase;
  scope: ReleaseStructureScope;
  /** Every scope this item covers (a whole-project root forked later lists each fork). */
  scopes: ReleaseStructureScope[];
  /** Primary release (drives the portal pointer / open link). */
  release: PortalFileRelease;
  bundle: PortalReleaseBundle | null;
  releases: PortalFileRelease[];
  status: TrailItemStatus;
  approval: PortalDeliverableApproval | null;
  approvalRequested: boolean;
  releasedAt: Date;
  releaseVersionIndex: number;
  displayLabel: string;
  releasedUrl: string;
  loomUrl?: string;
  /** Portal deliverable type for the legacy approval modal. */
  deliverableType: 'plans' | 'model' | 'concept';
  /**
   * Set when this 3D item is one of several current lanes that are copies of the
   * same upload. Lanes stay separate; one signature covers the pending labels.
   */
  sharedModelGroup?: SharedModelGroup;
}

export interface TrailStation extends TrailStationKey {
  id: string;
  label: string;
  status: TrailStationStatus;
  /** Current item at this stop (own or inherited). */
  item: TrailItem | null;
  /** True when `item` is inherited from the trunk / a parent lane. */
  inherited: boolean;
  /**
   * An earlier sign-off that still covers this stop while `item` is a newer
   * release asking for another signature. Null when the station itself shows
   * the approval.
   */
  priorApproval: TrailItem | null;
}

export interface TrailLane {
  key: string;
  label: string;
  structureIds: string[];
  structureNames: string[];
  /** Parent lane key (a later split), or null for top-level lanes under the trunk. */
  parentKey: string | null;
  isTrunk: boolean;
  stations: TrailStation[];
  approvedCount: number;
  totalCount: number;
  /** Index of the stop needing attention (first awaiting, else first pending); -1 when complete. */
  currentIndex: number;
  /** Label of that stop. Stops within a phase are parallel, so this is "outstanding", not "next". */
  nextLabel: string | null;
  /** "3 of 4 approved · Engineering 3D outstanding" */
  summary: string;
  /** Earliest release in this lane (used to order lanes and place the fork). */
  startedAt: Date | null;
}

export interface TrailScopeGroup {
  key: string;
  label: string;
  structureIds: string[];
  structureNames: string[];
  /** Newest first. */
  items: TrailItem[];
}

export interface TrailPhaseGroup {
  phase: PortalDesignPhase;
  label: string;
  scopes: TrailScopeGroup[];
}

export interface TrailNextAction {
  item: TrailItem;
  laneLabel: string;
  stationLabel: string;
}

export interface TrailCounts {
  total: number;
  approved: number;
  awaiting: number;
  informational: number;
  superseded: number;
}

/** structureId → stationId → status, so "is the Garage approved?" has an answer even when it was approved as part of a set. */
export type TrailCoverage = Record<string, Record<string, TrailStationStatus>>;

export interface ApprovalTrail {
  items: TrailItem[];
  /** Stations that exist anywhere on the project, canonical order. */
  stations: TrailStationKey[];
  trunk: TrailLane | null;
  lanes: TrailLane[];
  /** When the whole-project trunk forked into structure lanes. */
  forkAt: Date | null;
  phases: TrailPhaseGroup[];
  nextAction: TrailNextAction | null;
  counts: TrailCounts;
  coverage: TrailCoverage;
  /** Nothing released at all — callers hide the trail. */
  isEmpty: boolean;
}

export interface BuildApprovalTrailInput {
  releases: PortalFileRelease[];
  bundles?: PortalReleaseBundle[];
  legacyApprovals?: PortalApprovalTargetGroup;
  /** Proposal structures in proposal order — orders lanes and seeds coverage for whole-project approvals. */
  structures?: Array<{ id: string; name: string }>;
  /**
   * Files-tab structure groups (Interior Trusses, Decorative Oak, …). When a
   * group's structures are still one set, the trail draws that group instead of
   * a row per structure. A release that covers only some of a group stays at
   * the structure level.
   */
  structureGroups?: Array<{ label: string; structureIds: string[]; structureNames?: string[] }>;
  /** Hub files / streams when available (staff) — enables fork-inferred scopes for whole-project roots. */
  files?: ProjectFile[];
  streams?: ProjectFileStream[];
  /**
   * Temporary: a later 3D release deletes the portal modelApproval slot, so the
   * concept model signed before the proposal no longer carries a stamp. When
   * this date is set and no concept model still has a signature, the latest
   * concept model released at or before the signature is shown as approved.
   */
  proposalApprovedAt?: Date | null;
  /** Name on the signed proposal, used when the model signature itself was cleared. */
  proposalApprovedByName?: string | null;
}

const EMPTY_LEGACY: PortalApprovalTargetGroup = { plans: [], model: [], concept: [] };

function mediaOf(kind: PortalFileRelease['streamKind']): TrailMedia {
  return kind === 'three_d_html' ? '3d' : '2d';
}

function deliverableTypeFor(release: PortalFileRelease): 'plans' | 'model' | 'concept' {
  const target = release.portalTarget || (release.streamKind === 'three_d_html' ? 'model' : 'engineered_drawings');
  if (target === 'model') return 'model';
  if (target === 'concept_elevations') return 'concept';
  return 'plans';
}

function time(d: Date | null | undefined): number {
  return d instanceof Date ? d.getTime() : 0;
}

function isSuperset(a: string[], b: string[]): boolean {
  if (a.length <= b.length) return false;
  const set = new Set(a);
  return b.every(id => set.has(id));
}

function streamsForStructureSet(structureIds: string[], streams: ProjectFileStream[]): ProjectFileStream[] {
  const key = structureSetKey(structureIds);
  if (key === structureSetKey([])) return [];
  return streams.filter(stream => structureSetKey(stream.structureIds ?? []) === key);
}

/**
 * When this structure set was added after the original split. Whole-project
 * sign-offs from before that moment do not cover it. Null when the set was
 * part of the split, or when there are no tracks to date it.
 */
function structureSetAddedAt(structureIds: string[], streams: ProjectFileStream[]): Date | null {
  const mine = streamsForStructureSet(structureIds, streams);
  if (mine.length === 0) return null;
  const forks = mine.filter(stream => !!stream.forkedFromStreamId);
  if (forks.length > 0) {
    const cutoffs: Date[] = [];
    for (const stream of forks) {
      const cutoff = effectiveInheritVersionsAfter(stream, streams);
      if (!cutoff) return null;
      cutoffs.push(cutoff);
    }
    return cutoffs.reduce((earliest, cutoff) => (cutoff.getTime() < earliest.getTime() ? cutoff : earliest));
  }
  const splitTimes = streams
    .filter(stream => stream.forkedFromStreamId && !effectiveInheritVersionsAfter(stream, streams))
    .map(stream => (stream.createdAt instanceof Date ? stream.createdAt.getTime() : NaN))
    .filter(stamp => Number.isFinite(stamp));
  if (splitTimes.length === 0) return null;
  const created = mine
    .map(stream => (stream.createdAt instanceof Date ? stream.createdAt.getTime() : NaN))
    .filter(stamp => Number.isFinite(stamp));
  if (created.length !== mine.length) return null;
  const earliest = Math.min(...created);
  if (earliest - Math.min(...splitTimes) > 60_000) return new Date(earliest);
  return null;
}

/** A release tagged to this set at publish time. Fork-inferred copies of an older whole-project file do not count. */
function releaseOwnsScope(item: TrailItem, scopeKey: string): boolean {
  const tagged = (ids?: string[]) => {
    const key = structureSetKey(ids ?? []);
    return key === scopeKey && key !== structureSetKey([]);
  };
  if (item.releases.some(release => tagged(release.structureIds))) return true;
  if (item.bundle && tagged(item.bundle.structureIds)) return true;
  return false;
}

function scopesForMembers(members: PortalFileRelease[], ctx: ResolveScopeContext): ReleaseStructureScope[] {
  const out: ReleaseStructureScope[] = [];
  const seen = new Set<string>();
  for (const member of members) {
    for (const scope of resolveReleaseStructureScopes(member, ctx)) {
      if (seen.has(scope.key)) continue;
      seen.add(scope.key);
      out.push(scope);
    }
  }
  return out.length > 0 ? out : [wholeProjectScope()];
}

function groupByScope(members: PortalFileRelease[], ctx: ResolveScopeContext): PortalFileRelease[][] {
  const buckets = new Map<string, PortalFileRelease[]>();
  for (const member of members) {
    const key = scopesForMembers([member], ctx).map(scope => scope.key).join('|');
    const list = buckets.get(key) ?? [];
    list.push(member);
    buckets.set(key, list);
  }
  return Array.from(buckets.values());
}

export function buildApprovalTrail(input: BuildApprovalTrailInput): ApprovalTrail {
  const releases = input.releases.filter(r => !!r.id);
  const bundles = input.bundles ?? [];
  const legacy = input.legacyApprovals ?? EMPTY_LEGACY;
  const structures = input.structures ?? [];
  const ctx = { files: input.files ?? [], streams: input.streams ?? [] };
  const structureOrder = new Map<string, number>();
  structures.forEach((s, i) => structureOrder.set(s.id, i));

  // ── Items ──────────────────────────────────────────────────────────────────
  const releaseById = new Map<string, PortalFileRelease>();
  for (const r of releases) releaseById.set(r.id!, r);
  const bundleByReleaseId = new Map<string, PortalReleaseBundle>();
  for (const b of bundles) for (const rid of b.releaseIds) bundleByReleaseId.set(rid, b);
  for (const r of releases) {
    if (r.bundleId && !bundleByReleaseId.has(r.id!)) {
      const b = bundles.find(x => x.id === r.bundleId);
      if (b) bundleByReleaseId.set(r.id!, b);
    }
  }

  const items: TrailItem[] = [];
  const consumed = new Set<string>();
  for (const r of releases) {
    if (consumed.has(r.id!)) continue;
    const bundle = bundleByReleaseId.get(r.id!) ?? null;
    const members = bundle
      ? bundle.releaseIds.map(id => releaseById.get(id)).filter((x): x is PortalFileRelease => !!x)
      : [r];
    if (members.length === 0) members.push(r);
    for (const m of members) consumed.add(m.id!);
    const moved = members.filter(member => releaseMovedToStream(member, ctx));
    const stayed = members.filter(member => !releaseMovedToStream(member, ctx));
    // A file moved onto another structure leaves this review. The files that
    // stayed keep the original package. A package whose files all moved together
    // follows them as one review.
    const reviewGroups: { id: string; bundle: PortalReleaseBundle | null; members: PortalFileRelease[] }[] =
      moved.length > 0 && stayed.length > 0
        ? [
            { id: bundle?.id ?? stayed[0]!.id!, bundle, members: stayed },
            ...groupByScope(moved, ctx).map(group => ({
              id: `${bundle?.id ?? group[0]!.id!}:${scopesForMembers(group, ctx).map(scope => scope.key).join('|')}`,
              bundle: null,
              members: group,
            })),
          ]
        : [
            {
              id: bundle?.id ?? members[0]!.id!,
              bundle,
              members,
            },
          ];
    for (const group of reviewGroups) {
      const primary =
        (group.bundle && group.members.find(m => m.id === group.bundle!.primaryReleaseId)) || group.members[0]!;
      const phase = portalReleaseDesignPhase(group.bundle ?? primary);
      const scopes = group.bundle && moved.length === 0
        ? resolveBundleStructureScopes(group.bundle, group.members, ctx)
        : scopesForMembers(group.members, ctx);
      const ownApproval =
        (group.bundle?.approval?.status === 'approved' ? group.bundle.approval : null) ??
        group.members.map(m => m.approval).find(a => a?.status === 'approved') ??
        null;
      const legacyApproval = ownApproval ? null : findClientApprovalForFileReleases(legacy, group.members) ?? null;
      const approval = ownApproval ?? legacyApproval;
      const requestedFlags = [group.bundle?.approvalRequested, ...group.members.map(m => m.approvalRequested)];
      // Only an explicit request asks for a signature. Missing and false are informational.
      const approvalRequested = requestedFlags.some(f => f === true);
      const releasedAt = primary.releasedAt instanceof Date ? primary.releasedAt : new Date(0);
      const singleMoved = !group.bundle && group.members.length === 1 ? group.members[0]! : null;
      items.push({
        id: group.id,
        media: mediaOf(primary.streamKind),
        phase,
        scope: scopes[0] ?? wholeProjectScope(),
        scopes,
        release: primary,
        bundle: group.bundle,
        releases: group.members,
        status: approval ? 'approved' : approvalRequested ? 'awaiting' : 'informational',
        approval,
        approvalRequested,
        releasedAt,
        releaseVersionIndex: group.bundle?.releaseVersionIndex ?? primary.releaseVersionIndex,
        displayLabel: singleMoved?.displayLabel || group.bundle?.displayLabel || primary.displayLabel || primary.name,
        releasedUrl: primary.releasedUrl,
        loomUrl: primary.loomUrl,
        deliverableType: deliverableTypeFor(primary),
      });
    }
  }
  items.sort((a, b) => time(b.releasedAt) - time(a.releasedAt));

  // Dashed "superseded" only when a later release of the same stop exists and this
  // one was never approved. An older sign-off stays approved in the history.
  const newerReleaseExists = (it: TrailItem): boolean => items.some(other => {
    if (other === it || other.phase !== it.phase || other.media !== it.media) return false;
    if (time(other.releasedAt) <= time(it.releasedAt)) return false;
    return other.scopes.some(scope => it.scopes.some(own => own.key === scope.key));
  });
  for (const it of items) {
    if (it.status === 'approved') continue;
    if (newerReleaseExists(it)) it.status = 'superseded';
  }

  const signedAt = time(input.proposalApprovedAt);
  const conceptModels = items.filter(it => it.phase === 'concept' && it.media === '3d');
  if (signedAt > 0 && !conceptModels.some(it => it.status === 'approved')) {
    const covered = conceptModels
      .filter(it => time(it.releasedAt) > 0 && time(it.releasedAt) <= signedAt)
      .sort((a, b) => time(b.releasedAt) - time(a.releasedAt));
    if (covered[0]) {
      covered[0].status = 'approved';
      if (!covered[0].approval) {
        covered[0].approval = {
          status: 'approved',
          approvedByName: (input.proposalApprovedByName || '').trim(),
          approvedAt: input.proposalApprovedAt ?? undefined,
          signature: '',
        };
      }
    }
  }

  // ── Stations present on the project ────────────────────────────────────────
  const presentStations = new Set<string>();
  for (const it of items) presentStations.add(trailStationId({ phase: it.phase, media: it.media }));
  const stations = TRAIL_STATION_ORDER.filter(k => presentStations.has(trailStationId(k)));

  // ── Scopes → lanes ─────────────────────────────────────────────────────────
  const scopeByKey = new Map<string, ReleaseStructureScope>();
  for (const it of items) for (const s of it.scopes) if (!scopeByKey.has(s.key)) scopeByKey.set(s.key, s);
  const wholeKey = structureSetKey([]);

  const scopeSortIndex = (s: ReleaseStructureScope): number => {
    if (s.structureIds.length === 0) return -1;
    let best = Number.MAX_SAFE_INTEGER;
    for (const id of s.structureIds) {
      const i = structureOrder.get(id);
      if (i !== undefined && i < best) best = i;
    }
    return best;
  };
  // Once any release is scoped to structures, every other structure still gets a
  // lane so a pre-split sign-off stays visible. Named files-tab groups collapse
  // to one lane when nothing has split them apart.
  const hasStructureRelease = Array.from(scopeByKey.values()).some(s => s.key !== wholeKey);
  const groupedIds = new Set<string>();
  if (hasStructureRelease) {
    for (const group of input.structureGroups ?? []) {
      const ids = Array.from(new Set((group.structureIds ?? []).map(id => (id || '').trim()).filter(Boolean)));
      const label = (group.label || '').trim();
      if (!ids.length || !label) continue;
      const groupKey = structureSetKey(ids);
      const idSet = new Set(ids);
      const intersecting = Array.from(scopeByKey.values()).filter(
        scope => scope.key !== wholeKey && scope.structureIds.some(id => idSet.has(id)),
      );
      if (!intersecting.every(scope => scope.key === groupKey)) continue;
      const nameById = new Map<string, string>();
      (group.structureIds ?? []).forEach((raw, i) => {
        const id = (raw || '').trim();
        const name = (group.structureNames?.[i] || '').trim();
        if (id && name && !nameById.has(id)) nameById.set(id, name);
      });
      const names = ids.map(id => nameById.get(id) || id);
      scopeByKey.set(groupKey, {
        key: groupKey,
        structureIds: ids,
        structureNames: names,
        label,
      });
      ids.forEach(id => groupedIds.add(id));
    }
    const covered = new Set<string>(groupedIds);
    for (const scope of Array.from(scopeByKey.values())) {
      if (scope.key === wholeKey) continue;
      for (const id of scope.structureIds) if (id) covered.add(id);
    }
    for (const structure of structures) {
      const id = (structure.id || '').trim();
      if (!id || covered.has(id)) continue;
      const key = structureSetKey([id]);
      if (scopeByKey.has(key)) continue;
      const name = (structure.name || '').trim() || id;
      scopeByKey.set(key, {
        key,
        structureIds: [id],
        structureNames: [name],
        label: structureSelectionLabel([name]),
      });
    }
  }

  const scopedScopes = Array.from(scopeByKey.values())
    .filter(s => s.key !== wholeKey)
    .sort((a, b) => {
      const d = scopeSortIndex(a) - scopeSortIndex(b);
      return d !== 0 ? d : a.label.localeCompare(b.label);
    });

  // Current (non-superseded) item per (scopeKey, stationId).
  const currentByScopeStation = new Map<string, TrailItem>();
  for (const it of items) {
    if (it.status === 'superseded') continue;
    for (const s of it.scopes) {
      const k = `${s.key}|${trailStationId(it)}`;
      const prev = currentByScopeStation.get(k);
      if (!prev || time(it.releasedAt) > time(prev.releasedAt)) currentByScopeStation.set(k, it);
    }
  }

  /** Newest approved item whose scopes intersect `keys` at this stop. */
  const newestApproved = (stationId: string, keys: Set<string>): TrailItem | null => {
    let best: TrailItem | null = null;
    for (const it of items) {
      if (it.status !== 'approved') continue;
      if (trailStationId(it) !== stationId) continue;
      if (!it.scopes.some(s => keys.has(s.key))) continue;
      if (!best || time(it.releasedAt) > time(best.releasedAt)) best = it;
    }
    return best;
  };

  const parentKeyOf = (scope: ReleaseStructureScope): string | null => {
    let parent: ReleaseStructureScope | null = null;
    for (const other of scopedScopes) {
      if (other.key === scope.key) continue;
      if (!isSuperset(other.structureIds, scope.structureIds)) continue;
      if (!parent || other.structureIds.length < parent.structureIds.length) parent = other;
    }
    return parent ? parent.key : null;
  };

  const buildLane = (scope: ReleaseStructureScope, isTrunk: boolean, chain: string[]): TrailLane => {
    const laneStations: TrailStation[] = [];
    let startedAt: Date | null = null;
    const addedAt = isTrunk ? null : structureSetAddedAt(scope.structureIds, ctx.streams);
    const appliesToLane = (candidate: TrailItem): boolean => {
      if (!addedAt || releaseOwnsScope(candidate, scope.key)) return true;
      return time(candidate.releasedAt) >= time(addedAt);
    };
    for (const k of stations) {
      const sid = trailStationId(k);
      let item = currentByScopeStation.get(`${scope.key}|${sid}`) ?? null;
      if (item && !appliesToLane(item)) item = null;
      let inherited = false;
      if (!item && !isTrunk) {
        for (const ancestorKey of chain) {
          const candidate = currentByScopeStation.get(`${ancestorKey}|${sid}`);
          if (candidate && appliesToLane(candidate)) {
            item = candidate;
            inherited = true;
            break;
          }
        }
      }
      // A later informational release must not erase an earlier sign-off. A later
      // release that asks for a signature stays the station, and the sign-off is
      // kept beside it as priorApproval.
      const coverKeys = new Set<string>([scope.key, ...chain]);
      const coveringApproval = newestApproved(sid, coverKeys);
      const coveringApplies = !!coveringApproval && appliesToLane(coveringApproval);
      let priorApproval: TrailItem | null = null;
      if (coveringApplies && coveringApproval && item?.id !== coveringApproval.id) {
        if (!item || (item.status !== 'awaiting' && item.status !== 'approved')) {
          const own = coveringApproval.scopes.some(s => s.key === scope.key) && releaseOwnsScope(coveringApproval, scope.key);
          item = coveringApproval;
          inherited = !isTrunk && !own;
        } else if (item.status === 'awaiting') {
          priorApproval = coveringApproval;
        }
      }
      let status: TrailStationStatus = 'pending';
      if (item) {
        if (inherited) status = item.status === 'approved' ? 'shared' : item.status === 'awaiting' ? 'awaiting' : 'informational';
        else status = item.status === 'approved' ? 'approved' : item.status === 'awaiting' ? 'awaiting' : 'informational';
      }
      if (isTrunk && !item) continue; // trunk only draws stops it actually passed
      laneStations.push({
        ...k,
        id: sid,
        label: trailStationLabel(k),
        status,
        item,
        inherited,
        priorApproval,
      });
    }
    for (const it of items) {
      if (!it.scopes.some(s => s.key === scope.key)) continue;
      if (!appliesToLane(it)) continue;
      if (!startedAt || time(it.releasedAt) < time(startedAt)) startedAt = it.releasedAt;
    }
    const approvedCount = laneStations.filter(s => s.status === 'approved' || s.status === 'shared').length;
    const totalCount = laneStations.length;
    let currentIndex = laneStations.findIndex(s => s.status === 'awaiting');
    if (currentIndex < 0) currentIndex = laneStations.findIndex(s => s.status === 'pending' || s.status === 'informational');
    const nextLabel = currentIndex >= 0 ? laneStations[currentIndex]!.label : null;
    const summary =
      totalCount === 0
        ? 'Nothing released yet'
        : nextLabel
          ? `${approvedCount} of ${totalCount} approved · ${nextLabel} outstanding`
          : 'All approved';
    return {
      key: scope.key,
      label: isTrunk ? WHOLE_PROJECT_SELECTION_LABEL : scope.label,
      structureIds: scope.structureIds,
      structureNames: scope.structureNames,
      parentKey: isTrunk ? null : parentKeyOf(scope),
      isTrunk,
      stations: laneStations,
      approvedCount,
      totalCount,
      currentIndex,
      nextLabel,
      summary,
      startedAt,
    };
  };

  const hasWhole = scopeByKey.has(wholeKey);
  const lanes: TrailLane[] = scopedScopes.map(scope => {
    // Inheritance chain: nearest parent lanes first, then the trunk.
    const chain: string[] = [];
    let p = parentKeyOf(scope);
    while (p) {
      chain.push(p);
      const parentScope = scopeByKey.get(p);
      p = parentScope ? parentKeyOf(parentScope) : null;
    }
    if (hasWhole) chain.push(wholeKey);
    return buildLane(scope, false, chain);
  });
  const trunk = hasWhole ? buildLane(scopeByKey.get(wholeKey)!, true, []) : null;
  attachSharedModelGroups(items, lanes, trunk);

  let forkAt: Date | null = null;
  if (trunk && lanes.length > 0) {
    for (const l of lanes) if (l.startedAt && (!forkAt || time(l.startedAt) < time(forkAt))) forkAt = l.startedAt;
  }

  // ── Phase detail ───────────────────────────────────────────────────────────
  const phases: TrailPhaseGroup[] = [];
  for (const phase of PORTAL_DESIGN_PHASES) {
    const phaseItems = items.filter(it => it.phase === phase);
    if (phaseItems.length === 0) continue;
    const groups = new Map<string, TrailScopeGroup>();
    for (const it of phaseItems) {
      for (const s of it.scopes) {
        const g = groups.get(s.key) ?? {
          key: s.key,
          label: s.structureIds.length === 0 ? WHOLE_PROJECT_SELECTION_LABEL : s.label,
          structureIds: s.structureIds,
          structureNames: s.structureNames,
          items: [],
        };
        g.items.push(it);
        groups.set(s.key, g);
      }
    }
    const ordered = Array.from(groups.values()).sort((a, b) => {
      if (a.key === wholeKey) return -1;
      if (b.key === wholeKey) return 1;
      const d = scopeSortIndex(scopeByKey.get(a.key)!) - scopeSortIndex(scopeByKey.get(b.key)!);
      return d !== 0 ? d : a.label.localeCompare(b.label);
    });
    phases.push({ phase, label: PORTAL_DESIGN_PHASE_LABELS[phase], scopes: ordered });
  }

  // ── Next action ────────────────────────────────────────────────────────────
  let nextAction: TrailNextAction | null = null;
  const allLanes = [...(trunk ? [trunk] : []), ...lanes];
  const awaiting = items
    .filter(it => it.status === 'awaiting')
    .sort((a, b) => {
      const sa = TRAIL_STATION_ORDER.findIndex(k => k.phase === a.phase && k.media === a.media);
      const sb = TRAIL_STATION_ORDER.findIndex(k => k.phase === b.phase && k.media === b.media);
      return sa !== sb ? sa - sb : time(a.releasedAt) - time(b.releasedAt);
    });
  if (awaiting.length > 0) {
    const it = awaiting[0]!;
    const lane = allLanes.find(l => l.key === it.scope.key) ?? null;
    nextAction = {
      item: it,
      laneLabel: lane ? lane.label : it.scope.structureIds.length === 0 ? WHOLE_PROJECT_SELECTION_LABEL : it.scope.label,
      stationLabel: trailStationLabel(it),
    };
  }

  // ── Counts ─────────────────────────────────────────────────────────────────
  const counts: TrailCounts = { total: items.length, approved: 0, awaiting: 0, informational: 0, superseded: 0 };
  for (const it of items) counts[it.status] += 1;

  // ── Coverage ───────────────────────────────────────────────────────────────
  const coverage: TrailCoverage = {};
  const mark = (structureId: string, stationId: string, status: TrailStationStatus) => {
    const row = coverage[structureId] ?? (coverage[structureId] = {});
    const prev = row[stationId];
    const rank: Record<TrailStationStatus, number> = { pending: 0, informational: 1, awaiting: 2, shared: 3, approved: 3 };
    if (!prev || rank[status] > rank[prev]) row[stationId] = status === 'shared' ? 'approved' : status;
  };
  const allStructureIds = new Set<string>(structures.map(s => s.id));
  for (const l of lanes) for (const id of l.structureIds) allStructureIds.add(id);
  for (const l of lanes) for (const st of l.stations) for (const id of l.structureIds) mark(id, st.id, st.status);
  if (trunk) {
    for (const st of trunk.stations) {
      allStructureIds.forEach(id => {
        // A lane with its own item at this stop overrides the trunk; otherwise the trunk covers it.
        const lane = lanes.find(l => l.structureIds.indexOf(id) !== -1);
        const laneStation = lane?.stations.find(s => s.id === st.id);
        const own = laneStation && !laneStation.inherited && laneStation.item;
        // A structure added after this sign-off keeps a blank stop. Do not paint the trunk approval onto it.
        if (!own && laneStation?.item) mark(id, st.id, st.status);
        else if (!lane) mark(id, st.id, st.status);
      });
    }
  }

  return {
    items,
    stations,
    trunk,
    lanes,
    forkAt,
    phases,
    nextAction,
    counts,
    coverage,
    isEmpty: items.length === 0,
  };
}

/** Short label for a lane scope (re-exported so UI code has one import). */
export function trailScopeLabel(structureNames: string[]): string {
  return structureSelectionLabel(structureNames);
}

/**
 * Link current 3D items that are copies of one upload. Does not merge lanes.
 * Informational and superseded copies stay on their own.
 */
function attachSharedModelGroups(items: TrailItem[], lanes: TrailLane[], trunk: TrailLane | null): void {
  const labelFor = (scopeKey: string, fallback: string): string => {
    const lane = lanes.find(item => item.key === scopeKey) || (trunk?.key === scopeKey ? trunk : null);
    return lane?.label || fallback;
  };
  const buckets = new Map<string, TrailItem[]>();
  for (const item of items) {
    if (item.media !== '3d' || item.status === 'superseded') continue;
    if (item.status !== 'awaiting' && item.status !== 'approved') continue;
    const key = (item.release.sourceStructureReleaseId || '').trim();
    if (!key) continue;
    const bucketKey = `${item.phase}\n${key}`;
    const list = buckets.get(bucketKey) ?? [];
    list.push(item);
    buckets.set(bucketKey, list);
  }
  for (const [bucketKey, list] of Array.from(buckets.entries())) {
    const byScope = new Map<string, TrailItem>();
    for (const item of list) {
      const prev = byScope.get(item.scope.key);
      if (!prev || time(item.releasedAt) > time(prev.releasedAt)) byScope.set(item.scope.key, item);
    }
    const current = Array.from(byScope.values());
    if (current.length < 2) continue;
    const key = bucketKey.slice(bucketKey.indexOf('\n') + 1);
    const pendingLabels: string[] = [];
    const approvedLabels: string[] = [];
    for (const item of current) {
      const label = labelFor(item.scope.key, item.scope.label);
      if (item.status === 'approved') approvedLabels.push(label);
      else pendingLabels.push(label);
    }
    const group: SharedModelGroup = { key, pendingLabels, approvedLabels };
    for (const item of current) item.sharedModelGroup = group;
  }
}
